import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { ConsentDocument } from "@/components/ConsentDocument";
import { ConsentActions } from "@/components/ConsentActions";
import { Button } from "@/components/ui/button";
import { printConsent, fmtLong } from "@/lib/consentTemplate";
import { LOGO } from "@/lib/helpers";
import { Download, PauseCircle } from "lucide-react";

export const useSuspension = () =>
  useQuery({ queryKey: ["suspension-me"], queryFn: async () => (await api.get("/suspension/me")).data, staleTime: 60000 });

export const ConsentGate = () => {
  const qc = useQueryClient();
  const { data } = useSuspension();
  if (!data?.required) return null;
  const c = data.consent;
  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-navy/95 backdrop-blur-sm" data-testid="consent-gate">
      <div className="mx-auto my-6 max-w-3xl px-3 sm:px-6">
        <div className="flex items-center gap-3 rounded-t-2xl bg-gold px-5 py-4 text-navy">
          <img src={LOGO} alt="VNS" className="h-10 w-10 rounded bg-white p-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 font-heading text-sm font-bold"><PauseCircle className="h-4 w-4" />Action required before you continue</div>
            <div className="text-xs">Data entry operations are suspended until {fmtLong(data.suspension.end_date)}. Please read and respond.</div>
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto bg-white px-5 py-6 sm:px-8">
          <ConsentDocument consent={c} />
        </div>
        <div className="space-y-3 rounded-b-2xl border-t border-slate-200 bg-slate-50 px-5 py-5 sm:px-8">
          <Button variant="outline" size="sm" onClick={() => printConsent(c)} data-testid="consent-download-btn">
            <Download className="mr-2 h-4 w-4" />Download / print a copy
          </Button>
          <ConsentActions onDone={() => qc.invalidateQueries({ queryKey: ["suspension-me"] })} />
        </div>
      </div>
    </div>
  );
};

export const SuspensionBanner = () => {
  const { data } = useSuspension();
  if (!data?.suspension?.active) return null;
  return (
    <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs sm:text-sm text-amber-900 no-print" data-testid="suspension-banner">
      <PauseCircle className="h-4 w-4 shrink-0" />
      <span>Data entry operations are suspended until <b>{fmtLong(data.suspension.end_date)}</b>. New tasks and punch-in are paused; all records are kept.</span>
    </div>
  );
};
